import { jwtDecode } from "jwt-decode";
import { requestApi } from "@/shared/utills/http";
import { getAzureToken } from "./msal";

/**
 * Обмен Azure-токена на внутренний JWT — как на staffing. Сами запросы идут с Azure-токеном,
 * внутренний JWT нужен только ради claims пользователя: type, teamlead, open_review и т.д.
 */
export const AZURE_EXCHANGE_PATH = "/auth/azure/exchange";

/** Схема ответа обмена свободная: токен ищем в этих полях по очереди. */
const TOKEN_FIELDS = ["access_token", "accessToken", "token"] as const;

/** Не удалось получить или разобрать внутренний JWT. Текст показывается пользователю на экране логина. */
export class InternalJwtError extends Error {
  /** Исходная ошибка — для консоли и Sentry (lib проекта ES2020, стандартного Error.cause в типах нет). */
  readonly cause?: unknown;

  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = "InternalJwtError";
    this.cause = options?.cause;
  }
}

export const extractInternalJwt = (payload: unknown): string => {
  if (payload && typeof payload === "object") {
    for (const field of TOKEN_FIELDS) {
      const value = (payload as Record<string, unknown>)[field];

      if (typeof value === "string" && value) return value;
    }
  }

  throw new InternalJwtError(
    `Sign-in failed: the server response has no token (expected one of: ${TOKEN_FIELDS.join(", ")}).`,
  );
};

let cachedToken: string | null = null;
let inFlight: Promise<string> | null = null;

const exchangeAzureToken = async (): Promise<string> => {
  const azureToken = await getAzureToken();
  let payload: unknown;

  try {
    payload = await requestApi<unknown>({
      path: AZURE_EXCHANGE_PATH,
      method: "POST",
      headers: { Authorization: `Bearer ${azureToken}` },
      // обмен авторизуется Azure-токеном сам — провайдер авторизации запросов здесь не участвует
      withAuth: false,
    });
  } catch (error) {
    throw new InternalJwtError(
      "The server did not accept your Microsoft sign-in. Try again, and contact the administrator if it keeps failing.",
      { cause: error },
    );
  }

  return extractInternalJwt(payload);
};

/**
 * Внутренний JWT. Живёт только в памяти: после перезагрузки его заново выдаёт обмен по Azure-токену.
 * Параллельные вызовы делят один запрос обмена.
 */
export const getInternalJwt = (): Promise<string> => {
  if (cachedToken) return Promise.resolve(cachedToken);

  inFlight ??= exchangeAzureToken()
    .then((token) => {
      cachedToken = token;
      return token;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** Сброс после выхода или истёкшей сессии: следующий getInternalJwt сходит за новым токеном. */
export const resetInternalJwt = () => {
  cachedToken = null;
};

/** Payload JWT без проверки подписи — её проверяет бэкенд; фронту нужны только claims для интерфейса. */
export const parseInternalJwt = <T>(token: string): T => {
  try {
    return jwtDecode<T>(token);
  } catch (error) {
    throw new InternalJwtError("Sign-in failed: the token is malformed.", { cause: error });
  }
};
